import { useState, useEffect, useRef } from "react";
export default function BenchmarkProof() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, {
      threshold: 0.1
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const benchmarks = [{
    label: "Claude 3.5 Sonnet",
    fill: 95,
    score: "89.5",
    highlight: false
  }, {
    label: "GPT-4o",
    fill: 94,
    score: "89.2",
    highlight: false
  }, {
    label: "Orchid 1.0 · 2B",
    fill: 93,
    score: "87.9",
    highlight: true
  }, {
    label: "BitNet b1.58 · 2B",
    fill: 88,
    score: "84.2",
    highlight: false
  }, {
    label: "Kimi k1.5",
    fill: 86,
    score: "82.2",
    highlight: false
  }, {
    label: "Qwen2.5 · 7B",
    fill: 81,
    score: "78.4",
    highlight: false
  }];
  return <section style={{
    backgroundPosition: "0% 0%",
    backgroundRepeat: "repeat",
    color: "rgb(244, 244, 243)",
    fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
    animation: "none 0s ease 0s 1 normal none running",
    backgroundColor: "#0b0b0d"
  }} className="w-full">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .benchmark-link:hover {
          opacity: 0.75;
        }
        .benchmark-card {
          background: #141417;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 16px;
          padding: 26px;
          transition: 160ms;
        }
        .benchmark-card:hover {
          border-color: rgba(255,255,255,0.16);
          background: #1a1a1e;
          transform: translateY(-2px);
        }
      `}</style>
      <div ref={ref} className="page-container" style={{
      paddingTop: "132px",
      paddingBottom: "132px"
    }}>
        <div className={`flex flex-col md:flex-row items-center gap-12 md:gap-20 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="w-full md:w-2/5 flex flex-col items-start">
            <div className="flex flex-row items-center gap-2 mb-3">
              <div style={{
              width: "10px",
              height: "10px",
              backgroundColor: "#f5c518",
              flexShrink: 0
            }} />
              <span style={{
              fontFamily: '"Geist Mono", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
              fontSize: "11px",
              fontWeight: 400,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#76767d"
            }}>
                THE PROOF
              </span>
            </div>

            <h2 style={{
            color: "rgb(244, 244, 243)",
            fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
            fontSize: "41.6px",
            fontWeight: "600",
            lineHeight: "43.264px",
            letterSpacing: "-1.248px",
            border: "0px none rgb(244, 244, 243)",
            animation: "none 0s ease 0s 1 normal none running",
            marginBottom: "20px"
          }}>
              A 2B model in a
              <br />
              7B+ fight.
            </h2>

            <p style={{
            color: "rgb(185, 185, 189)",
            fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
            fontSize: "17px",
            fontWeight: "400",
            lineHeight: "27.2px",
            letterSpacing: "-0.187px",
            border: "0px none rgb(185, 185, 189)",
            animation: "none 0s ease 0s 1 normal none running",
            marginBottom: "16px"
          }}>
              Internal Benchmark v2 — 100 questions across 8 categories, semantic similarity scoring. Orchid lands{" "}
              <strong style={{
              fontWeight: 700,
              color: "#f4f4f3"
            }}>third of twelve models</strong>, ahead of every open-weight system, including Qwen2.5-7B and Kimi k1.5.
            </p>

            <p style={{
            color: "rgb(118, 118, 125)",
            fontFamily: '"Geist Mono", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
            fontSize: "12.8px",
            fontWeight: "400",
            lineHeight: "20.48px",
            letterSpacing: "-0.187px",
            border: "0px none rgb(118, 118, 125)",
            animation: "none 0s ease 0s 1 normal none running",
            marginBottom: "28px"
          }}>
              Science 100% · Math 93.3% · Coding 93.3%
            </p>

            <a href="#" className="benchmark-link cursor-pointer transition-opacity duration-150" style={{
            color: "rgb(244, 244, 243)",
            fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
            fontSize: "15.36px",
            fontWeight: "540",
            lineHeight: "24.576px",
            letterSpacing: "-0.1536px",
            borderRadius: "10px",
            border: "1px solid rgba(0, 0, 0, 0)",
            padding: "11.0592px 4.608px",
            animation: "none 0s ease 0s 1 normal none running",
            textDecoration: "none",
            display: "inline-block"
          }}>
              See full benchmarks →
            </a>
          </div>

          <div className="w-full md:w-3/5">
            <div className="benchmark-card">
              <div className="flex flex-row items-center justify-between" style={{
              marginBottom: "20px"
            }}>
                <span style={{
                fontFamily: '"Geist Mono", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: "11px",
                fontWeight: 400,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#76767d"
              }}>
                  INTERNAL BENCHMARK V2
                </span>
                <div className="flex flex-row items-center" style={{
                gap: "6px"
              }}>
                  <div style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#f5c518",
                  flexShrink: 0
                }} />
                  <span style={{
                  fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#f4f4f3"
                }}>
                    Orchid 1.0
                  </span>
                </div>
              </div>

              <div className="flex flex-col" style={{
              gap: "18px"
            }}>
                {benchmarks.map((row, i) => <div key={i} className="flex flex-row items-center" style={{
                gap: "12px"
              }}>
                    <span style={{
                  fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "1.4",
                  color: row.highlight ? "#f5c518" : "#b9b9bd",
                  minWidth: "130px",
                  flexShrink: 0
                }}>
                      {row.label}
                    </span>
                    <div className="flex-1" style={{
                  height: "6px",
                  borderRadius: "3px",
                  backgroundColor: "#2a2a2e",
                  overflow: "hidden"
                }}>
                      <div style={{
                    width: `${row.fill}%`,
                    height: "100%",
                    borderRadius: "3px",
                    backgroundColor: row.highlight ? "#f5c518" : "#4a4a51"
                  }} />
                    </div>
                    <span style={{
                  fontFamily: '"Geist Mono", "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
                  fontSize: "14px",
                  fontWeight: 400,
                  color: row.highlight ? "#f5c518" : "#b9b9bd",
                  minWidth: "40px",
                  textAlign: "right",
                  flexShrink: 0
                }}>
                      {row.score}
                    </span>
                  </div>)}
              </div>

              <p style={{
              color: "rgb(118, 118, 125)",
              fontFamily: 'Geist, "Hanken Grotesk", system-ui, -apple-system, sans-serif',
              fontSize: "12.8px",
              fontWeight: "400",
              lineHeight: "20.48px",
              letterSpacing: "-0.187px",
              border: "0px none rgb(118, 118, 125)",
              animation: "none 0s ease 0s 1 normal none running",
              marginTop: "18px",
              marginBottom: 0
            }}>
                Semantic-similarity scoring is a relative comparison tool, not a substitute for standard NLP benchmarks.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}